import { useEffect, useRef, useState, type FormEvent } from 'react';
import QRCode from 'qrcode';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { AuthChallenge, SignInResult } from '@/lib/auth/cognito';
import { totpUri } from '@/lib/auth/cognito';
import { isMfaSessionExpired, mfaErrorMessage, newPasswordErrorMessage } from '@/lib/auth/mfa-errors';

interface MfaStepProps {
  challenge: AuthChallenge;
  email: string;
  /** App name an authenticator app lists the account under. */
  appName: string;
  /** Receives the result of a step: a session, or the next challenge. */
  onResult: (result: SignInResult) => void;
  /** The Cognito session behind the step lapsed; the parent returns to the
   * password form and shows the line. */
  onExpired: (message: string) => void;
  onCancel: () => void;
}

/**
 * The steps between the password form and a session: a new password, the
 * authenticator setup (QR code, key, first code) and the code prompt on
 * later sign-ins. Labels are one or two words and no step carries helper
 * copy. A refused code clears the field and shows one line.
 */
export function MfaStep({ challenge, email, appName, onResult, onExpired, onCancel }: MfaStepProps) {
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [secret, setSecret] = useState('');
  const [qr, setQr] = useState('');
  const started = useRef(false);
  const isSetup = challenge.kind === 'mfa-setup';
  const isPassword = challenge.kind === 'new-password';

  useEffect(() => {
    if (!isSetup || started.current || !challenge.begin) return;
    // Once only: each call issues a new key and replaces the session the
    // verify step answers on (StrictMode runs this effect twice in dev).
    started.current = true;
    (async () => {
      try {
        const key = await challenge.begin!();
        setSecret(key);
        setQr(await QRCode.toDataURL(totpUri(email, key, appName), { margin: 1, width: 192 }));
      } catch (err) {
        if (isMfaSessionExpired(err)) {
          onExpired(mfaErrorMessage(err));
          return;
        }
        setError(mfaErrorMessage(err));
      }
    })();
  }, [isSetup, challenge, email, appName, onExpired]);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    setError('');
    setBusy(true);
    try {
      const result = await challenge.submit(isPassword ? value : value.replace(/\s+/g, ''));
      onResult(result);
    } catch (err) {
      if (isMfaSessionExpired(err)) {
        onExpired(mfaErrorMessage(err));
        return;
      }
      setError(isPassword ? newPasswordErrorMessage(err) : mfaErrorMessage(err));
      setValue('');
    } finally {
      setBusy(false);
    }
  }

  const waitingForKey = isSetup && !secret && !error;

  return (
    <form className="flex flex-col gap-4" onSubmit={onSubmit} data-testid={`step-${challenge.kind}`}>
      {isSetup ? (
        <div className="flex flex-col items-center gap-3">
          <h2 className="text-sm font-medium text-surface-foreground">Authenticator</h2>
          {qr ? (
            <img src={qr} alt="Authenticator QR code" width={192} height={192} className="rounded-control bg-white" />
          ) : (
            <div className="size-48 rounded-control border border-border" aria-hidden />
          )}
          {secret ? (
            <div className="flex w-full flex-col gap-1.5">
              <Label htmlFor="mfa-key">Key</Label>
              <p
                id="mfa-key"
                className="select-all break-all rounded-control border border-border px-3 py-2 font-mono text-sm text-surface-foreground"
              >
                {secret}
              </p>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="mfa-input">{isPassword ? 'New password' : 'Code'}</Label>
        {isPassword ? (
          <Input
            id="mfa-input"
            name="new-password"
            type="password"
            autoComplete="new-password"
            required
            value={value}
            onChange={(event) => setValue(event.target.value)}
          />
        ) : (
          <Input
            id="mfa-input"
            name="code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9 ]*"
            maxLength={7}
            required
            autoFocus={!isSetup}
            value={value}
            onChange={(event) => setValue(event.target.value)}
          />
        )}
      </div>

      {error ? (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : null}

      <Button type="submit" size="lg" disabled={busy || waitingForKey || value.trim() === ''}>
        {busy ? 'Verifying…' : isPassword ? 'Continue' : 'Verify'}
      </Button>
      <Button type="button" variant="ghost" onClick={onCancel} disabled={busy}>
        Cancel
      </Button>
    </form>
  );
}
